import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

// ─── Theme definitions ────────────────────────────────────────────────────────

/**
 * Available palette identifiers. 'green' is the default (no extra class needed).
 * 'purple' is retained as a code-valid legacy value (ADR-4) but deliberately
 * excluded from `UI_THEMES` / the `themes` list exposed to the UI.
 */
export type ThemeId = 'green' | 'blue' | 'purple' | 'slate' | 'bronze' | 'berry' | 'contrast';

/** Full set of code-valid palette values — governs setTheme/readStoredTheme validity. */
const FULL_THEMES: readonly ThemeId[] = ['green', 'blue', 'purple', 'slate', 'bronze', 'berry', 'contrast'];

/**
 * UI-reachable palette list only (design.md §6 order) — exposed as `themes`.
 * Purple is deliberately excluded here so neither the picker nor cycleTheme
 * can ever land on it (ADR-4).
 */
const UI_THEMES: readonly ThemeId[] = ['green', 'blue', 'slate', 'bronze', 'berry', 'contrast'];

/** Fallback palette when no valid value is stored. */
const DEFAULT_THEME: ThemeId = 'green';

const THEME_LABEL: Readonly<Record<ThemeId, string>> = {
  green: 'Green (default)',
  blue: 'Blue',
  purple: 'Purple',
  slate: 'Slate',
  bronze: 'Bronze',
  berry: 'Berry',
  contrast: 'Contrast',
};

const STORAGE_KEY = 'ic-theme';
const MODE_STORAGE_KEY = 'ic-theme-mode';

/** Mode axis — independent of palette (ADR-8/ADR-9). */
export type ThemeMode = 'light' | 'dark';

// ─── Context ──────────────────────────────────────────────────────────────────

interface ThemeContextValue {
  readonly theme: ThemeId;
  readonly themes: readonly ThemeId[];
  readonly themeLabel: (id: ThemeId) => string;
  readonly setTheme: (id: ThemeId) => void;
  readonly cycleTheme: () => void;
  readonly mode: ThemeMode;
  readonly setMode: (mode: ThemeMode) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Reads a localStorage key, swallowing unavailability (e.g. private browsing). */
function safeLocalStorageGet(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

/** Writes a localStorage key, swallowing write errors (e.g. private browsing quota). */
function safeLocalStorageSet(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Ignore write errors (e.g. private browsing quota)
  }
}

function readStoredTheme(): ThemeId {
  const stored = safeLocalStorageGet(STORAGE_KEY);
  if (stored && (FULL_THEMES as ReadonlyArray<string>).includes(stored)) {
    return stored as ThemeId;
  }
  return DEFAULT_THEME;
}

/** Reads an explicit stored mode. Returns null if absent, invalid, or unreadable — INV-1 extended to the mode axis. */
function readStoredMode(): ThemeMode | null {
  const stored = safeLocalStorageGet(MODE_STORAGE_KEY);
  if (stored === 'light' || stored === 'dark') {
    return stored;
  }
  return null;
}

const PREFERS_DARK_QUERY = '(prefers-color-scheme: dark)';

/** The OS dark-mode media query, or undefined if matchMedia is unavailable. Never throws (INV-3 spirit). */
function getPrefersDarkMediaQuery(): MediaQueryList | undefined {
  try {
    if (typeof globalThis.matchMedia !== 'function') {
      return undefined;
    }
    return globalThis.matchMedia(PREFERS_DARK_QUERY);
  } catch {
    return undefined;
  }
}

/** Live OS-level dark-mode preference. Never throws (robustness, INV-3 spirit). */
function getSystemPrefersDark(): boolean {
  return getPrefersDarkMediaQuery()?.matches ?? false;
}

// ─── Provider ─────────────────────────────────────────────────────────────────

export function ThemeProvider({ children }: Readonly<{ children: ReactNode }>) {
  const [theme, setTheme] = useState<ThemeId>(readStoredTheme);
  const [explicitMode, setExplicitMode] = useState<ThemeMode | null>(readStoredMode);
  const [systemDark, setSystemDark] = useState<boolean>(getSystemPrefersDark);

  const mode: ThemeMode = explicitMode ?? (systemDark ? 'dark' : 'light');

  // Live-track the OS-level prefers-color-scheme media query for as long as
  // no explicit mode choice has been persisted (ADR-9). Once an explicit
  // choice exists, `explicitMode` takes precedence in the `mode` computation
  // above regardless of further OS-level changes.
  useEffect(() => {
    const mql = getPrefersDarkMediaQuery();
    if (!mql) return;

    const handleChange = (e: { matches: boolean }) => setSystemDark(e.matches);

    mql.addEventListener('change', handleChange);

    return () => {
      mql.removeEventListener('change', handleChange);
    };
  }, []);

  // Sync data-theme / data-palette attributes on <html> whenever either axis
  // changes. This entirely replaces the old classList.add(THEME_CLASS[...])
  // mechanism — no `.theme-*` class is ever applied.
  useEffect(() => {
    const root = document.documentElement;
    root.dataset.palette = theme;
    root.dataset.theme = mode;
  }, [theme, mode]);

  const applyTheme = useCallback((id: ThemeId) => {
    setTheme(id);
    safeLocalStorageSet(STORAGE_KEY, id);
  }, []);

  const applyMode = useCallback((next: ThemeMode) => {
    setExplicitMode(next);
    safeLocalStorageSet(MODE_STORAGE_KEY, next);
  }, []);

  const cycleTheme = useCallback(() => {
    setTheme((current) => {
      const idx = UI_THEMES.indexOf(current);
      // Fallback covers the (unreachable in practice) case where `current` isn't
      // found in UI_THEMES, mirroring the AVATAR_PALETTE indexing convention in
      // TopBar.tsx rather than asserting the index is always in bounds.
      const next: ThemeId = UI_THEMES[(idx + 1) % UI_THEMES.length] ?? DEFAULT_THEME;
      safeLocalStorageSet(STORAGE_KEY, next);
      return next;
    });
  }, []);

  const themeLabel = useCallback((id: ThemeId) => THEME_LABEL[id], []);

  const contextValue = useMemo(
    () => ({ theme, themes: UI_THEMES, themeLabel, setTheme: applyTheme, cycleTheme, mode, setMode: applyMode }),
    [theme, themeLabel, applyTheme, cycleTheme, mode, applyMode],
  );

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
}
